import React, { useEffect, useState } from 'react';
import { Button, Modal } from '@mui/material';
import { message, Spin } from 'antd';
import { apis } from 'utils/lib/apis';

const contentStyle: React.CSSProperties = {
    alignItems: 'stretch',
    backgroundColor: '#fff',
    borderRadius: 3,
    display: 'inline-flex',
    flexDirection: 'column',
    left: '50%',
    maxWidth: 'min(520px, 95vw)',
    padding: 16,
    position: 'absolute',
    textAlign: 'left',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 320,
};

const rowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 12,
    width: '100%',
    gap: 8,
};

const labelStyle: React.CSSProperties = {
    fontSize: 12,
    color: '#666',
    fontWeight: 600,
};

const valueStyle: React.CSSProperties = {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    padding: '8px 10px',
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    fontSize: 14,
};

const actionsRowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 16,
    width: '100%',
    justifyContent: 'center',
    gap: 8,
};

export interface PasswordViewerUser {
    id: string;
    username: string;
}

export interface PasswordViewerModalProps {
    open: boolean;
    user: PasswordViewerUser | null;
    onClose: () => void;
}

const PasswordViewerModal: React.FC<PasswordViewerModalProps> = ({ open, user, onClose }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const fetchPassword = async () => {
            if (!open || !user) {
                setPassword('');
                setError('');
                setShowPassword(false);
                return;
            }
            setLoading(true);
            setError('');
            setPassword('');
            setShowPassword(false);
            try {
                const response = await apis.post('usersRDS', '/get_user_password', {
                    body: {
                        id: user.id,
                        username: user.username,
                    },
                });
                if (response && response.success && response.data && response.data.password) {
                    setPassword(String(response.data.password));
                } else {
                    setError(`No password is stored for username:- ${user.username}.`);
                }
            } catch {
                setError('Failed to fetch password');
            } finally {
                setLoading(false);
            }
        };
        fetchPassword();
    }, [open, user]);

    return (
        <Modal open={open} onClose={onClose}>
            <div style={contentStyle}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <Spin size="large" />
                        <div style={{ ...rowStyle, marginTop: 8 }}>Loading password...</div>
                    </div>
                ) : error ? (
                    <div
                        style={{
                            padding: 16,
                            backgroundColor: '#fff1f0',
                            border: '1px solid #ffccc7',
                            borderRadius: 4,
                            color: '#cf1322',
                            textAlign: 'center',
                        }}
                    >
                        {error}
                    </div>
                ) : (
                    <div style={{ width: '100%' }}>
                        {!!password && (
                            <>
                                <div style={rowStyle}>
                                    <span style={labelStyle}>Username</span>
                                    <div style={valueStyle}>{user?.username ?? ''}</div>
                                </div>
                                <div style={rowStyle}>
                                    <span style={labelStyle}>Password</span>
                                    <div style={valueStyle}>{showPassword ? password : '********'}</div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? 'Hide' : 'Show'}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                if (password) {
                                                    navigator.clipboard.writeText(password);
                                                    message.success('Password copied to clipboard');
                                                }
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                <div style={actionsRowStyle}>
                    <Button color="primary" variant="contained" style={{ marginRight: 4 }} onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PasswordViewerModal;
